import {
    TableProvider,
    TableHeader,
    TableActiveColumnFilters,
    Table,
    useFullRowSelection,
    StackItem,
    Stack,
} from '@optimus-web/core';
import { lt, useUrlSyncedTableState } from '@mews/framework';
import {
    PortfolioCentralProductOverviewEntriesFilters,
    PortfolioCentralProductOverviewEntry,
} from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useGetCentralProductOverviewEntries } from '../../../queries/CentralProductsTable';
import { CentralProductSidePanelContextProvider } from '../../CentralProductSidePanel/CentralProductSidePanelContext';
import { tableEmptyState } from '../../_shared/tableEmptyState';
import { useNamesFromIds } from '../../../hooks/useNamesFromIds';
import { uniqueByKeys } from '../../../utils/uniqueByKeys';
import { EnterprisesProductContextProvider } from '../../../context/EnterprisesProductContext';
import { BulkActions } from './BulkActions';
import { centralProductTableColumns } from './columns';
import { ProductsQuotaProgressChart } from './ProductsQuotaProgressChart';

export type PortfolioCentralProductOverviewEntryColumns = {
    properties: string[];
    propertyGroups: string[];
} & PortfolioCentralProductOverviewEntry;

export const CentralProductTable = () => {
    const columns = centralProductTableColumns;

    const [tableState, setTableState] = useUrlSyncedTableState<
        PortfolioCentralProductOverviewEntryColumns,
        PortfolioCentralProductOverviewEntriesFilters
    >({
        columns,
        pageSize: 25,
    });

    const enterpriseId = useCurrentEnterpriseId();

    const { data: centralProductsEntries, isFetching } = useGetCentralProductOverviewEntries(tableState, enterpriseId);
    const { data, totalRowCount } = centralProductsEntries || {};

    const uniquePropertyIds = uniqueByKeys(['propertyIds'], data);
    const uniquePropertyGroupIds = uniqueByKeys(['propertyGroupIds'], data);

    const { propertiesLookupTable, propertyGroupsLookupTable } = useNamesFromIds(
        uniquePropertyIds.propertyIds,
        uniquePropertyGroupIds.propertyGroupIds
    );

    const mergedData = data?.map((product) => ({
        ...product,
        properties: propertiesLookupTable
            ? product.propertyIds.map((propertyId) => propertiesLookupTable[propertyId])
            : [],
        propertyGroups: propertyGroupsLookupTable
            ? product.propertyGroupIds.map((propertyGroupId) => propertyGroupsLookupTable[propertyGroupId])
            : [],
    }));

    const { selectedRowsData, rowSelection, setRowSelection, resetRowSelection } = useFullRowSelection(data);

    return (
        <CentralProductSidePanelContextProvider>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                <Stack distribution="space-between">
                    <TableHeader title={lt('ProductPlural')} subTitle={totalRowCount || '0'} />
                    <StackItem style={{ paddingBottom: '2em', minWidth: '300px' }}>
                        <ProductsQuotaProgressChart enterpriseId={enterpriseId} />
                    </StackItem>
                </Stack>
                <TableActiveColumnFilters />
                <EnterprisesProductContextProvider>
                    <BulkActions selectedRows={selectedRowsData} resetRowSelection={resetRowSelection} />
                    <Table
                        loading={isFetching}
                        uniqueRowId="id"
                        data={mergedData ?? []}
                        amountOfRows={totalRowCount ?? 0}
                        rowSelection={{
                            rowSelection,
                            onRowSelectionChange: setRowSelection,
                        }}
                        fixedFirstColumn
                        fixedLastColumn
                        emptyState={tableEmptyState}
                    />
                </EnterprisesProductContextProvider>
            </TableProvider>
        </CentralProductSidePanelContextProvider>
    );
};
